import { AGENT_TILE_WIDTH, AgentTemplateTile } from '@/components/AgentTile';
import { ContentBar } from '@/pages/home/components/Content/ContentBar';
import { IAgent } from '@d-id/types';
import { theme } from '@de-id/ui-components';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

const SectionTitle = styled.h2`
    color: ${({ theme }) => theme.colors.black[700]};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.32px;
    margin: 32px 0 16px;

    display: flex;
    align-items: center;
    gap: 1rem;
`;

const HiddenReferenceGrid = styled.div`
    position: absolute;
    visibility: hidden;
    pointer-events: none;
    top: -9999px;
    left: -9999px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${AGENT_TILE_WIDTH}px, 1fr));
    grid-gap: 24px;
    width: 100%;
`;

const HiddenTile = styled.div`
    width: 100%;
    height: 100px;
`;

const AgentsContainer = styled.div<{ tileWidth?: number }>`
    display: flex;
    gap: 24px;

    > * {
        flex: 0 0 ${({ tileWidth }) => tileWidth || AGENT_TILE_WIDTH}px;
        width: ${({ tileWidth }) => tileWidth || AGENT_TILE_WIDTH}px;
        min-width: ${({ tileWidth }) => tileWidth || AGENT_TILE_WIDTH}px;
        max-width: ${({ tileWidth }) => tileWidth || AGENT_TILE_WIDTH}px;
    }
`;

const NewTag = styled(Typography)`
    color: ${theme.colors.success[750]};
    background: #ddf7e7;
    border-radius: 6px;
    padding: 2px 4px;
    font-family: Manrope;
    font-weight: 500;
    font-size: 10px;
    line-height: 16px;
    letter-spacing: 0.15px;
    text-transform: uppercase;
`;

interface Props {
    agents: IAgent[];
    clientKey: any;
}

export const TemplatesList = ({ agents, clientKey }: Props) => {
    const hiddenGridRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [calculatedTileWidth, setCalculatedTileWidth] = useState<number>(AGENT_TILE_WIDTH);

    useEffect(() => {
        const calculateTileWidth = () => {
            if (hiddenGridRef.current && containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                hiddenGridRef.current.style.width = `${containerWidth}px`;

                hiddenGridRef.current.offsetHeight;

                const firstTile = hiddenGridRef.current.querySelector('div');
                if (firstTile) {
                    const tileWidth = firstTile.getBoundingClientRect().width;
                    setCalculatedTileWidth(Math.floor(tileWidth));
                }
            }
        };

        calculateTileWidth();
        window.addEventListener('resize', calculateTileWidth);

        return () => window.removeEventListener('resize', calculateTileWidth);
    }, []);

    return (
        <div ref={containerRef}>
            <SectionTitle>
                Explore Agent templates
                <NewTag>NEW</NewTag>
            </SectionTitle>

            <HiddenReferenceGrid ref={hiddenGridRef}>
                <HiddenTile />
                <HiddenTile />
                <HiddenTile />
                <HiddenTile />
            </HiddenReferenceGrid>

            <ContentBar id="agent-templates-list-section">
                <AgentsContainer tileWidth={calculatedTileWidth}>
                    {agents.map(agent => (
                        <AgentTemplateTile key={agent.id} template={agent} clientKey={clientKey?.client_key} />
                    ))}
                </AgentsContainer>
            </ContentBar>
        </div>
    );
};
